import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {Subscription} from "rxjs";
import {SessionModel} from "../../../orchestration/session.model";
import {ResponsiveService} from "../../../orchestration/responsive.service";
import {SessionEvents} from "../../../orchestration/session-events";
import {PaymentModel} from "../../payment.model";
import {PaymentEvents} from "../../payment-events";
import {CardPaymentSubmissionResult, PaymentAuthorisationType, PaymentCard} from "../../payment";

declare type StripeFieldStatus = {
    brand: string;
    complete: boolean;
    elementType: string;
    empty: boolean;
    error: {
        code: string;
        type: string;
        message: string;
    };
    value: string;
};

@Component({
    selector: "woa-stripe-manual-payment-card-panel",
    templateUrl: "./stripe-manual-payment-card-panel.component.html",
    providers: [{provide: PaymentCard, useExisting: StripeManualPaymentCardPanelComponent}]
})
export class StripeManualPaymentCardPanelComponent extends PaymentCard implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() disabled: boolean = false;

    @ViewChild("cardNumber") cardNumberField: ElementRef;
    @ViewChild("cardExpiry") cardExpiryField: ElementRef;
    @ViewChild("cardCvc") cardCvcField: ElementRef;

    savePaymentDetails: boolean = false;

    private stripe: any;
    private cardNumberElement: any;
    private cardExpiryElement: any;
    private cardCvcElement: any;
    private cardNumberStatus: StripeFieldStatus;
    private cardExpiryStatus: StripeFieldStatus;
    private cardCvcStatus: StripeFieldStatus;
    private submitted: boolean = false;
    private subscriptions: Subscription = new Subscription();

    constructor(private element: ElementRef,
                private sessionModel: SessionModel,
                private changeDetectorRef: ChangeDetectorRef,
                private responsiveService: ResponsiveService,
                private paymentModel: PaymentModel) {
        super();
        this.savePaymentDetails = this.responsiveService.isMobile || this.sessionModel.devOrDemoMode;
    }

    get showSavePaymentDetailsOption(): boolean {
        return !this.sessionModel.waiterMode;
    }

    get cardNumberError(): string {
        return this.cardNumberStatus && this.cardNumberStatus.error ? "'" + this.cardNumberStatus.error.message + "'"
            : (this.cardNumberStatus && this.cardNumberStatus.empty) || (this.submitted && !this.cardNumberStatus) ? "woa.validation.common.required"
                : undefined;
    }

    get cardExpiryError(): string {
        return this.cardExpiryStatus && this.cardExpiryStatus.error ? "'" + this.cardExpiryStatus.error.message + "'"
            : (this.cardExpiryStatus && this.cardExpiryStatus.empty) || (this.submitted && !this.cardExpiryStatus) ? "woa.validation.common.required"
                : undefined;
    }

    get cardCvcError(): string {
        return this.cardCvcStatus && this.cardCvcStatus.error ? "'" + this.cardCvcStatus.error.message + "'"
            : (this.cardCvcStatus && this.cardCvcStatus.empty) || (this.submitted && !this.cardCvcStatus) ? "woa.validation.common.required"
                : undefined;
    }

    ngOnInit(): void {
        PaymentEvents.paymentDetailsApplied$.subscribe(() => this.setup());
        this.subscriptions.add(SessionEvents.currentApp$.subscribe(() => {
            this.changeDetectorRef.detectChanges();
            this.styleCardElements();
        }));
    }

    ngAfterViewInit(): void {
        this.setup();
    }

    ngOnChanges(): void {
        this.setup();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    canSavePaymentDetails(): boolean {
        return this.showSavePaymentDetailsOption && this.savePaymentDetails;
    }

    isValid(): boolean {
        this.submitted = true;
        return !this.cardNumberError && !this.cardExpiryError && !this.cardCvcError;
    }

    markAsUnsubmitted(): void {
        this.submitted = false;
    }

    async submit(): Promise<CardPaymentSubmissionResult> {
        const result: any = await this.stripe.createPaymentMethod({type: "card", card: this.cardNumberElement});
        if (result.error) {
            throw result.error.message;
        }
        return new CardPaymentSubmissionResult(PaymentAuthorisationType.STRIPE_PAYMENT_METHOD, result.paymentMethod.id, result.paymentMethod.card.brand, undefined, result.paymentMethod.card.last4);
    }

    setup(): void {
        if (!this.stripe && this.paymentModel.paymentPlatformPublicKey && this.cardNumberField && this.cardExpiryField && this.cardCvcField) {
            // noinspection JSUnresolvedReference
            this.stripe = new (<any>window).Stripe(this.paymentModel.paymentPlatformPublicKey);
            const elements = this.stripe.elements();
            this.cardNumberElement = elements.create("cardNumber", {showIcon: true, placeholder: ""});
            this.cardExpiryElement = elements.create("cardExpiry", {placeholder: ""});
            this.cardCvcElement = elements.create("cardCvc", {placeholder: ""});
            setTimeout(() => this.styleCardElements());
            this.cardNumberElement.on("change", (status: StripeFieldStatus) => {
                this.cardNumberStatus = status;
                if (status.complete) {
                    this.cardExpiryElement.focus();
                }
            });
            this.cardExpiryElement.on("change", (status: StripeFieldStatus) => {
                this.cardExpiryStatus = status;
                if (status.complete) {
                    this.cardCvcElement.focus();
                }
            });
            this.cardCvcElement.on("change", (status: StripeFieldStatus) => {
                this.cardCvcStatus = status;
            });
            setTimeout(() => {
                // noinspection JSUnresolvedReference
                this.cardNumberElement.mount(this.cardNumberField.nativeElement);
                // noinspection JSUnresolvedReference
                this.cardExpiryElement.mount(this.cardExpiryField.nativeElement);
                // noinspection JSUnresolvedReference
                this.cardCvcElement.mount(this.cardCvcField.nativeElement);
            });
        }
    }

    styleCardElements(): void {
        if (this.cardNumberElement) {
            const color: string = getComputedStyle(this.element.nativeElement).getPropertyValue("--theme-color-pair-foreground");
            if (!color) {
                setTimeout(() => this.styleCardElements(), 100);
                return;
            }
            const font: string = getComputedStyle(this.element.nativeElement).getPropertyValue("--woa-theme-font-common");
            [this.cardNumberElement, this.cardExpiryElement, this.cardCvcElement].forEach((cardElement: any) => {
                cardElement.update({
                    style: {
                        base: {
                            fontFamily: font,
                            fontSize: "16px",
                            lineHeight: "19px",
                            iconColor: "rgb(" + color + ")",
                            color: "rgb(" + color + ")"
                        },
                        invalid: {
                            color: "rgb(" + color + ")",
                        }
                    }
                });
            });
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/payment/card-payment/stripe-manual-payment-card-panel/stripe-manual-payment-card-panel.component.ts